// src/pages/admin/Jobs/JobsList.jsx
import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppDispatch'
import {
    fetchJobsAdmin,
    clearCurrentJob,
    clearError
} from '../../../redux/slices/jobAdminSlice'
import { jobsService } from '../../../services/jobsService'
import SEO from '../../../components/seo/SEO'
import EmptyState from '../../../components/common/EmptyState'
import SkeletonTable from '../../../components/common/SkeletonTable'
import JobModal from './JobModal'
import ConfirmModal from '../../../components/common/ConfirmModal'
import Toast from '../../../components/common/Toast'
import Pagination from '../../../components/common/Pagination'

export default function JobsList() {
    const dispatch = useAppDispatch()
    const { jobs, loading, error, pagination } = useAppSelector((state) => state.jobsAdmin)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
    const [selectedJob, setSelectedJob] = useState(null)
    const [modalMode, setModalMode] = useState('create')
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' })
    const [filters, setFilters] = useState({
        search: '',
        type: '',
        status: '',
        page: 1,
        limit: 10
    })

    useEffect(() => {
        loadJobs()
    }, [filters])

    useEffect(() => {
        if (error) {
            setToast({
                show: true,
                message: typeof error === 'string' ? error : error?.message || 'An error occurred',
                type: 'error'
            })
            dispatch(clearError())
        }
    }, [error, dispatch])

    const loadJobs = () => {
        const params = { ...filters }
        // Convert status filter to isOpen boolean
        if (params.status === 'open') {
            params.isOpen = true
        } else if (params.status === 'closed') {
            params.isOpen = false
        }
        dispatch(fetchJobsAdmin(params))
        setTimeout(() => {
            console.log(jobs, 'DDDDDDDDDDDDDjobs')
        }, 3000);
    }

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type })
    }

    const handleCreateJob = () => {
        setSelectedJob(null)
        setModalMode('create')
        setIsModalOpen(true)
    }

    const handleViewJob = (job) => {
        setSelectedJob(job)
        setModalMode('view')
        setIsModalOpen(true)
    }

    const handleEditJob = (job) => {
        setSelectedJob(job)
        setModalMode('edit')
        setIsModalOpen(true)
    }

    const handleDeleteJob = (job) => {
        setSelectedJob(job)
        setIsConfirmModalOpen(true)
    }

    const handleToggleStatus = async (job) => {
        try {
            const updatedData = { isOpen: !job.isOpen }
            await jobsService.updateJob(job._id, updatedData)
            showToast(`Job ${!job.isOpen ? 'opened' : 'closed'} successfully`)
            loadJobs()
        } catch (error) {
            showToast('Failed to update job status', 'error')
            console.error('Failed to update job status:', error)
        }
    }

    const handleToggleFeatured = async (job) => {
        try {
            const updatedData = { featured: !job.featured }
            await jobsService.updateJob(job._id, updatedData)
            showToast(`Job ${!job.featured ? 'marked as featured' : 'unfeatured'} successfully`)
            loadJobs()
        } catch (error) {
            showToast('Failed to update job featured status', 'error')
            console.error('Failed to update job featured status:', error)
        }
    }

    const confirmDelete = async () => {
        if (selectedJob) {
            try {
                await jobsService.deleteJob(selectedJob._id)
                showToast('Job deleted successfully')
                setIsConfirmModalOpen(false)
                setSelectedJob(null)
                loadJobs()
            } catch (error) {
                showToast('Failed to delete job', 'error')
                console.error('Failed to delete job:', error)
            }
        }
    }

    const handleModalClose = (refresh = false) => {
        setIsModalOpen(false)
        setSelectedJob(null)
        dispatch(clearCurrentJob())

        if (refresh) {
            loadJobs()
        }
    }

    const handleJobCreated = () => {
        handleModalClose(true)
        showToast('Job created successfully')
    }

    const handleJobUpdated = () => {
        handleModalClose(true)
        showToast('Job updated successfully')
    }

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }))
    }

    const handlePageChange = (page) => {
        setFilters(prev => ({ ...prev, page }))
    }

    const handleLimitChange = (limit) => {
        setFilters(prev => ({ ...prev, limit, page: 1 }))
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        })
    }

    const getTypeColor = (type) => {
        const colors = {
            'Full-time': 'bg-blue-100 text-blue-800 border-blue-200',
            'Part-time': 'bg-green-100 text-green-800 border-green-200',
            'Contract': 'bg-yellow-100 text-yellow-800 border-yellow-200',
            'Remote': 'bg-purple-100 text-purple-800 border-purple-200',
            'Hybrid': 'bg-indigo-100 text-indigo-800 border-indigo-200',
            'On-Site': 'bg-red-100 text-red-800 border-red-200'
        }
        return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200'
    }

    return (
        <>
            <SEO title="Jobs Management" />

            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Career Opportunities</h1>
                        <p className="text-gray-600 mt-1">
                            Manage your job postings and career opportunities
                        </p>
                    </div>
                    <button
                        onClick={handleCreateJob}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium flex items-center transition-colors duration-200"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Create Job Posting
                    </button>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                            <input
                                type="text"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                placeholder="Search job titles..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                            <select
                                value={filters.type}
                                onChange={(e) => handleFilterChange('type', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="">All Types</option>
                                <option value="Full-time">Full-time</option>
                                <option value="Part-time">Part-time</option>
                                <option value="Contract">Contract</option>
                                <option value="Remote">Remote</option>
                                <option value="Hybrid">Hybrid</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="">All Status</option>
                                <option value="open">Open</option>
                                <option value="closed">Closed</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Items per page</label>
                            <select
                                value={filters.limit}
                                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Jobs Table */}
                <div className="bg-white shadow rounded-lg border border-gray-200">
                    {loading ? (
                        <div className="p-6">
                            <SkeletonTable rows={5} columns={7} />
                        </div>
                    ) : jobs && jobs.length > 0 ? (
                        <>
                            <div className="overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Job Position
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Location & Type
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Hero Spans
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Featured
                                            </th>
                                            {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th> */}
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Created
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {jobs.map((job) => (
                                            <tr key={job._id} className="hover:bg-gray-50 transition-colors duration-150">
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {job.title}
                                                        </div>
                                                        <div className="text-sm text-gray-500 font-mono">
                                                            /{job.slug}
                                                        </div>
                                                        {job.hero?.heading && (
                                                            <div className="text-xs text-gray-400 mt-1 line-clamp-1">
                                                                {job.hero.heading}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="space-y-1">
                                                        <div className="text-sm text-gray-900">{job.location}</div>
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeColor(job.type)}`}>
                                                            {job.type}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-wrap gap-1 max-w-xs">
                                                        {job.hero?.spans?.slice(0, 3).map((span, index) => (
                                                            <span key={index} className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full border border-gray-200">
                                                                {span?.value || 'No text'}
                                                            </span>
                                                        ))}
                                                        {job.hero?.spans?.length > 3 && (
                                                            <span className="text-xs text-gray-500">+{job.hero.spans.length - 3}</span>
                                                        )}
                                                        {(!job.hero?.spans || job.hero.spans.length === 0) && (
                                                            <span className="text-xs text-gray-400 italic">No spans</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <button
                                                        // onClick={() => handleToggleFeatured(job)}
                                                        className={`inline-flex cursor-text items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors duration-200 ${job.featured
                                                            ? 'bg-yellow-100 text-yellow-800 border border-yellow-200 hover:bg-yellow-200'
                                                            : 'bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200'
                                                            }`}
                                                    >
                                                        {job.featured ? '⭐ Featured' : 'Normal'}
                                                    </button>
                                                </td>
                                                {/* <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <button
                                                            onClick={() => handleToggleStatus(job)}
                                                            className={`mr-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${job.isOpen ? 'bg-green-500' : 'bg-gray-300'}`}
                                                        >
                                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${job.isOpen ? 'translate-x-6' : 'translate-x-1'}`} />
                                                        </button>
                                                        <span className={`text-sm font-medium ${job.isOpen ? 'text-green-700' : 'text-red-700'}`}>
                                                            {job.isOpen ? 'Open' : 'Closed'}
                                                        </span>
                                                    </div>
                                                </td> */}
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDate(job.createdAt)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end space-x-3">
                                                        <button
                                                            onClick={() => handleViewJob(job)}
                                                            className="text-primary-600 hover:text-primary-800 font-medium transition-colors duration-200"
                                                            title="View Details"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() => handleEditJob(job)}
                                                            className="text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
                                                            title="Edit Job"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteJob(job)}
                                                            className="text-red-600 hover:text-red-800 font-medium transition-colors duration-200"
                                                            title="Delete Job"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <div className="px-6 py-4 border-t border-gray-200">
                                    <Pagination
                                        currentPage={pagination.page}
                                        totalPages={pagination.totalPages}
                                        totalItems={pagination.total}
                                        itemsPerPage={pagination.limit}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="p-6">
                            <EmptyState
                                title="No Job Postings Found"
                                message="There are no job postings created yet. Start by creating your first career opportunity."
                                action="Create First Job"
                                onAction={handleCreateJob}
                            />
                        </div>
                    )}
                </div>

                {/* Help Text */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">
                                Jobs Management Guide
                            </h3>
                            <div className="mt-2 text-sm text-blue-700">
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Create comprehensive job postings with multiple sections</li>
                                    <li>Use hero spans to highlight key job details (location, type, experience)</li>
                                    <li>Mark jobs as featured to highlight them on the careers page</li>
                                    <li>Toggle job status to open/close applications</li>
                                    <li>Add detailed job descriptions, responsibilities, and requirements</li>
                                    <li>Include SEO information for better search visibility</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Job Modal */}
            <JobModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                job={selectedJob}
                mode={modalMode}
                onJobCreated={handleJobCreated}
                onJobUpdated={handleJobUpdated}
            />

            {/* Confirm Delete Modal */}
            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Job Posting"
                message={`Are you sure you want to delete "${selectedJob?.title}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />

            {/* Toast Notification */}
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ ...toast, show: false })}
                duration={3000}
            />
        </>
    )
}